import { PdfGen, PdfUnit, PdfSize, PdfRect, PdfBrush, PdfColor } from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/label';

/**
 * GuessWho (Guess Who Game)
 *
 * Layout: Grid of cards with colored circles for "Guess Who" style game.
 * Page 1: Answer grid (small cards)
 * Page 2-3: Question cards (larger, two halves - red and blue)
 *
 * Uses adjustFontMulti separately for left (questions) and right (answers) labels.
 *
 * Migrated from C# EduFrame.Single.Generators.GuessWhoBase
 */
export class GuessWho implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 12;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 24;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const count = Math.min(opts.list.length, 24);

    pdf.addPage();
    const page2 = pdf.addPage();
    const page3 = pdf.addPage();

    const redBrush = new PdfBrush(PdfColor.fromHex('#FF0000'));
    const blueBrush = new PdfBrush(PdfColor.fromHex('#0000FF'));

    this.drawHalf(pdf, 1, 0, redBrush, opts, count);
    this.drawHalf(pdf, 2, 1, blueBrush, opts, count);
  }

  private drawHalf(
    pdf: PdfGen,
    pageIndex: number,
    half: number,
    color: PdfBrush,
    opts: OptsLabel,
    totalCount: number
  ): void {
    const page1 = pdf.getPage(0);
    const pageQ = pdf.getPage(pageIndex);

    const size = new PdfSize(PdfUnit.FromMillimeters(23), PdfUnit.FromMillimeters(26));
    const margin = PdfUnit.FromMillimeters(10);
    const countX = Math.floor((PdfSize.A4.width - 2 * margin) / size.width);
    const countY = Math.ceil(totalCount / countX);

    const originX = (PdfSize.A4.width - countX * size.width) / 2;
    const originY = margin + (half * PdfSize.A4.height) / 2;

    const marginCard = PdfUnit.FromMillimeters(5);
    const questionGridX = 3;
    const questionGridY = 8;

    const items = opts.list.slice(0, totalCount);

    const leftTexts = items.map((item) => item.left.getText() ?? '');
    const rightTexts = items.map((item) => item.right.getText() ?? '');
    const leftTextsArray = leftTexts.map((t) => [t]);
    const rightTextsArray = rightTexts.map((t) => [t]);

    const fontRightPage1 = page1.adjustFontMulti(rightTextsArray, GUtil.baseFont, size);

    for (let y = 0; y < countY; y++) {
      for (let x = 0; x < countX; x++) {
        const index = y * countX + x;
        if (index >= totalCount) continue;

        const rect = new PdfRect(
          originX + x * size.width,
          originY + y * size.height,
          size.width,
          size.height
        );
        this.drawCircle(page1, rect, color);
        page1.drawRectangle(GUtil.cutPen, null, rect);
        page1.drawLabel(items[index].right, { rect, font: fontRightPage1 });
      }
    }

    const qSize = new PdfSize(PdfSize.A4.width / questionGridX, PdfSize.A4.height / questionGridY);
    const cellSize = new PdfSize(qSize.width - 2 * marginCard, qSize.height - 2 * marginCard);

    const fontLeft = pageQ.adjustFontMulti(leftTextsArray, GUtil.baseFont, cellSize);
    const fontRight = pageQ.adjustFontMulti(rightTextsArray, GUtil.baseFont, cellSize);

    for (let y = 0; y < questionGridY; y++) {
      for (let x = 0; x < questionGridX; x++) {
        const index = y * questionGridX + x;
        if (index >= totalCount) continue;

        const outerRect = new PdfRect(x * qSize.width, y * qSize.height, qSize.width, qSize.height);
        const rect = new PdfRect(
          x * qSize.width + marginCard,
          y * qSize.height + marginCard,
          qSize.width - 2 * marginCard,
          qSize.height - 2 * marginCard
        );

        pageQ.drawRectangle(GUtil.cutPen, null, outerRect);
        this.drawCircle(pageQ, rect, color);
        pageQ.drawLabel(items[index].left, { rect, font: fontLeft });
      }
    }
  }

  private drawCircle(page: any, rect: PdfRect, brush: PdfBrush): void {
    const m1 = PdfUnit.FromMillimeters(1);
    const s1 = PdfUnit.FromMillimeters(4);
    const r = new PdfRect(rect.x + m1, rect.y + m1, s1, s1);
    page.drawEllipse(null, brush, r);
  }
}
